// The numbers on a receive screen: expected, scanned, missing, extra (05 §5.2). Pure, so the
// arithmetic is tested without a screen.
import { viewScan, type LedgerEntry, type OpsCarrierItem, type OpsSession } from '@ecobox/firebase';

export interface ReceiveProgress {
  /** Boxes the server expects on the trip or in the container. */
  expected: number;
  /** Expected boxes seen so far: scanned on any phone (the server's view) or on this one. */
  scanned: number;
  /** Expected and not scanned yet: what `close` would raise as missing. */
  missing: number;
  /** Scanned but not on the list. */
  extra: number;
  /** Barcodes still to find, for the "find these" list. */
  missingCodes: string[];
}

/** An item the server lists as expected: not removed, and not an overage it added itself. */
function isExpected(item: OpsCarrierItem): boolean {
  return item.result !== 'removed' && item.result !== 'overage';
}

export function receiveProgress(
  items: readonly OpsCarrierItem[],
  session: OpsSession,
  ledger: ReadonlyMap<string, LedgerEntry>,
): ReceiveProgress {
  const expected = new Map(items.filter(isExpected).map((i) => [i.barcode, i]));
  const seen = new Set<string>();
  for (const item of items) {
    if (isExpected(item) && (item.receivedAt !== undefined || item.result === 'received')) {
      seen.add(item.barcode);
    }
  }
  let extra = items.filter((i) => i.result === 'overage').length;
  const extras = new Set(items.filter((i) => i.result === 'overage').map((i) => i.barcode));
  for (const scan of session.scans) {
    const view = viewScan(scan, scan.commandId ? ledger.get(scan.commandId) : undefined);
    if (!view.counts) continue;
    if (expected.has(scan.barcode)) seen.add(scan.barcode);
    else if (!extras.has(scan.barcode)) {
      extras.add(scan.barcode);
      extra += 1;
    }
  }
  const missingCodes = [...expected.keys()].filter((code) => !seen.has(code)).sort();
  return {
    expected: expected.size,
    scanned: seen.size,
    missing: missingCodes.length,
    extra,
    missingCodes,
  };
}
